import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import moment from "moment";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useAppUserAction } from "src/_common/hooks/actions/user/appUserActionHook";
import { APPLICATION_GLOBAL_SLUG, handleApiCall, DATE_FORMAT } from "src/_config";
import InputTextField from "src/components/common/Form/InputTextField";
import AuthModalHeader from "./AuthModalHeader";
import AuthModalFooter from "./AuthModalFooter";
import { useAppGameDetailsSelector } from "src/_common/hooks/selectors/eventSelector";
import DateInput from "src/_common/components/form-elements/datepicker/dateInput";
import FormMaskedInput from "src/_common/components/form-elements/maskedInput/maskedInput";
import ReactInputMask from "react-input-mask";

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegisterSuccess: () => void;
}

interface RegisterFormValues {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone: string;
  dob: string;
  display_name: string;
}

const getDynamicSchema = (fieldsData: any) => {
  const schema: any = {
    dob: yup.string().test("is-18-years-old", "You must be at least 18 years old", (value) => {
      if (!value) return true;
      const dob = moment(value);
      return moment().diff(dob, "years") >= 18;
    }),
  };
  fieldsData.forEach((field: any) => {
    if (field.is_required) {
      switch (field.field_name) {
        case "email":
          schema.email = yup.string().email("Please enter a valid email").required("Email is required");
          break;
        case "password":
          schema.password = yup.string().required("Password is required");
          break;
        case "first_name":
          schema.first_name = yup.string().required("First Name is required");
          break;
        case "last_name":
          schema.last_name = yup.string().required("Last Name is required");
          break;
        case "phone":
          schema.phone = yup.string().required("Contact Number is required");
          break;
        case "dob":
          schema.dob = yup
            .string()
            .required("Date of birth is required")
            .test("is-18-years-old", "You must be at least 18 years old", (value) => {
              if (!value) return false;
              const dob = moment(value);
              return moment().diff(dob, "years") >= 18;
            });
          break;
        case "display_name":
          schema.display_name = yup.string().required("Display Name is required");
          break;
        default:
          break;
      }
    }
  });
  return yup.object().shape(schema);
};

const RegisterModal: React.FC<RegisterModalProps> = ({ isOpen, onClose, onRegisterSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [subscribeUpdates, setSubscribeUpdates] = useState(false);
  const [registrationFieldsData, setRegistrationFieldsData] = useState<any>([]);

  // Hooks (same as register.tsx)
  const userApi = useUserApi();
  const toast = useToaster();
  const history = useHistory();
  const userAction = useAppUserAction();
  const gameDetails = useAppGameDetailsSelector();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RegisterFormValues>({
    resolver: yupResolver(getDynamicSchema(registrationFieldsData)),
    defaultValues: {
      email: "",
      password: "",
      first_name: "",
      last_name: "",
      phone: "",
      dob: "",
      display_name: "",
    },
  });

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const getRegistrationFieldsData = useCallback(() => {
    const data = {
      gameId: 1,
    };
    handleApiCall(
      userApi.callGetRegistrationFieldsData,
      data,
      (resp: any) => {
        if (resp?.fields) {
          setRegistrationFieldsData(resp?.fields);
        }
      },
      toast,
      false,
      true
    );
  }, [gameDetails?.id, userApi, toast]);

  const onSubmit = (data: any) => {
    if (!agreeToTerms) {
      toast.error("Please agree to the Terms of Service and Privacy Policy");
      return;
    }

    setIsLoading(true);

    // Prepare API data (same structure as register.tsx)
    if (data.dob) {
      data["dob_date"] = moment(data?.dob).format("DD");
      data["dob_month"] = moment(data?.dob).format("MM");
      data["dob_year"] = moment(data?.dob).format("YYYY");
    }
    delete data.dob;
    const mobileNoDigit = data.phone && data.phone.replace(/\D/g, "").slice(-10);
    if (mobileNoDigit) {
      data["phone"] = parseInt(mobileNoDigit) + "";
      data["phone_code"] = "1";
    }
    data["game_slug"] = APPLICATION_GLOBAL_SLUG;

    handleApiCall(
      userApi.callRegistration,
      data,
      (resp: any) => {
        setIsLoading(false);
        if (resp) {
          toast.success("Your registration has been successfully completed");
          userAction.setGuestUserId(null);

          // Handle redirect if exists
          const link = localStorage.getItem("redirectLink");
          if (link) {
            localStorage.removeItem("redirectLink");
            history.push(link);
          }

          // Call success callback
          onRegisterSuccess();
        }
      },
      toast,
      false,
      true,
      () => {
        setIsLoading(false);
      }
    );
  };

  const handleClose = () => {
    reset();
    setIsLoading(false);
    setShowPassword(false);
    setAgreeToTerms(false);
    setSubscribeUpdates(false);
    onClose();
  };

  useEffect(() => {
    getRegistrationFieldsData();
  }, [getRegistrationFieldsData]);

  useEffect(() => {
    // Reset form when registration fields data changes
    if (registrationFieldsData.length > 0) {
      reset();
    }
  }, [registrationFieldsData, reset]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal fade show" style={{ display: "block" }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content signin-modal">
            <div className="modal-header border-0">
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={handleClose}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body text-center">
              {/* Header with Logo */}
              <AuthModalHeader />
              {/* Lock Icon */}
              <div className="signin-icon mb-3">
                <div className="lock-icon">
                  <i className="fas fa-lock"></i>
                </div>
              </div>

              {/* Title */}
              <h2 className="signin-title mb-2">Create Account</h2>
              <p className="signin-subtitle mb-4">Welcome back to the arena</p>

              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Dynamic Fields */}
                {registrationFieldsData.map((field: any) => {
                  if (!field.is_enable) return null;
                  switch (field.field_name) {
                    case "first_name":
                      return (
                        <div className="email-input-container mb-3" key={field.field_id}>
                          <div className="input-group">
                            <span className="input-group-text email-modal-prefix">
                              <i className="fas fa-user"></i>
                            </span>
                            <InputTextField
                              fieldName="first_name"
                              control={control}
                              errors={errors}
                              placeholder="First Name"
                              fieldType="text"
                            />
                          </div>
                        </div>
                      );
                    case "last_name":
                      return (
                        <div className="email-input-container mb-3" key={field.field_id}>
                          <div className="input-group">
                            <span className="input-group-text email-modal-prefix">
                              <i className="fas fa-user"></i>
                            </span>
                            <InputTextField
                              fieldName="last_name"
                              control={control}
                              errors={errors}
                              placeholder="Last Name"
                              fieldType="text"
                            />
                          </div>
                        </div>
                      );
                    case "dob":
                      return (
                        <div className="email-input-container mb-3" key={field.field_id}>
                          <div className="input-group">
                            <span className="input-group-text email-modal-prefix">
                              <i className="fas fa-calendar"></i>
                            </span>
                            <Controller
                              control={control}
                              name="dob"
                              render={({ onChange, onBlur, value }) => (
                                <DateInput
                                  onChange={onChange}
                                  onBlur={onBlur}
                                  value={value}
                                  maxDate={new Date()}
                                  dateFormat={DATE_FORMAT}
                                  error={errors.dob}
                                  placeholder="MM/DD/YYYY"
                                  customInput={<ReactInputMask mask="99/99/9999" maskChar="_" />}
                                />
                              )}
                            />
                          </div>
                        </div>
                      );
                    case "phone":
                      return (
                        <div className="email-input-container mb-3" key={field.field_id}>
                          <div className="input-group">
                            <span className="input-group-text email-modal-prefix">
                              <i className="fas fa-phone"></i>
                            </span>
                            <Controller
                              control={control}
                              name="phone"
                              render={({ onChange, onBlur, value, name, ref }) => (
                                <FormMaskedInput
                                  name={name}
                                  mask={"(999) 999 9999"}
                                  maskChar=" "
                                  onChange={onChange}
                                  onBlur={onBlur}
                                  value={value}
                                  inputRef={ref}
                                  type="tel"
                                  placeholder="Contact No"
                                  error={errors.phone}
                                />
                              )}
                            />
                          </div>
                        </div>
                      );
                    case "email":
                      return (
                        <div className="email-input-container mb-3" key={field.field_id}>
                          <div className="input-group">
                            <span className="input-group-text email-modal-prefix">
                              <i className="fas fa-envelope"></i>
                            </span>
                            <InputTextField
                              fieldName="email"
                              control={control}
                              errors={errors}
                              placeholder="Enter your email address"
                              fieldType="email"
                            />
                          </div>
                        </div>
                      );
                    case "password":
                      return (
                        <div className="password-input-container mb-3 position-relative" key={field.field_id}>
                          <div className="input-group">
                            <span className="input-group-text email-modal-prefix">
                              <i className="fas fa-key"></i>
                            </span>
                            <div className="position-relative flex-grow-1">
                              <InputTextField
                                fieldName="password"
                                fieldType="password"
                                control={control}
                                errors={errors}
                                placeholder="Create a password"
                                showPassword={showPassword}
                                toggleShowPassword={toggleShowPassword}
                                eyeStyle={{ top: "12px", right: "12px", zIndex: 10 }}
                              />
                            </div>
                          </div>
                          <div className="password-hint mt-1">
                            <small className="text-muted">Must be at least 8 characters long</small>
                          </div>
                        </div>
                      );
                    case "display_name":
                      return (
                        <div className="email-input-container mb-3" key={field.field_id}>
                          <div className="input-group">
                            <span className="input-group-text email-modal-prefix">
                              <i className="fas fa-user-tag"></i>
                            </span>
                            <InputTextField
                              fieldName="display_name"
                              control={control}
                              errors={errors}
                              placeholder="Display Name"
                              fieldType="text"
                            />
                          </div>
                        </div>
                      );
                    default:
                      return null;
                  }
                })}

                {/* Subscribe to Updates Checkbox */}
                <div className="remember-forgot-container mb-3">
                  <div className="form-check text-start">
                    <input
                      className="form-check-input email-modal-checkbox"
                      type="checkbox"
                      id="subscribeUpdates"
                      checked={subscribeUpdates}
                      onChange={(e) => setSubscribeUpdates(e.target.checked)}
                    />
                    <label className="form-check-label email-modal-remember-text" htmlFor="subscribeUpdates">
                      Subscribe to updates & promotions
                    </label>
                  </div>
                </div>

                {/* Terms and Privacy Policy Checkbox */}
                <div className="remember-forgot-container mb-3">
                  <div className="form-check text-start">
                    <input
                      className="form-check-input email-modal-checkbox"
                      type="checkbox"
                      id="agreeToTerms"
                      checked={agreeToTerms}
                      onChange={(e) => setAgreeToTerms(e.target.checked)}
                    />
                    <label className="form-check-label email-modal-remember-text" htmlFor="agreeToTerms">
                      I agree to the{" "}
                      <a
                        href="/cms/terms-and-conditions"
                        className="btn btn-link p-0 forgot-password-btn"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a
                        href="/cms/privacy-policy"
                        className="btn btn-link p-0 forgot-password-btn "
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>
                    </label>
                  </div>
                </div>

                {/* Sign Up Button */}
                <button
                  type="submit"
                  className="btn btn-success w-100 mb-3 email-modal-login-btn"
                  disabled={isLoading || !agreeToTerms}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Creating Account...
                    </>
                  ) : (
                    "Sign Up"
                  )}
                </button>
              </form>

              {/* Already Have Account Link */}
              <div className="create-account-container">
                <span className="create-account-text">Already have an account? </span>
                <button className="btn btn-link create-account-btn p-0" onClick={onClose}>
                  Log in
                </button>
              </div>
              {/* Footer Links */}
              <AuthModalFooter />
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
};

export default RegisterModal;
